import * as React from 'react';
import { Button, Checkbox, StackLayout } from '@leagueplatform/genesis-core';
import { MoveItemsModal } from '@web-config-app/core-react-ui';
import { AnnotatedJsonSchema } from '@web-config-app/core';
import { getBreadcrumbsForProperty } from '@web-config-app/core-react';
import { useIntl } from '@leagueplatform/locales';
import {
  useEntityDetailsProps,
  generateMoveTargetsFromJsonPath,
} from '@web-config-app/entity-form';

export interface SelectableMoveArrayItemsProps<T> {
  isSelectionMode: boolean;
  /**
   * A map of the selected array items to move by index and label
   */
  selectedItems: Map<number, string>;
  handleMoveClick: () => void;
  handleCancelMove: () => void;
  handleSelectDestination: () => void;
  handleSelectAll: () => void;
  handleMoveItems: (targetPath: string) => void;
  isMoveItemsModalOpen: boolean;
  setIsMoveItemsModalOpen: (isOpen: boolean) => void;
  /**
   * Function to get the label/identifier for each item
   */
  getItemLabel: (item: T) => string;
  items: T[];
  schema: AnnotatedJsonSchema;
}

/**
 * This component is used to render the checkbox for each item in the array
 */
export const MoveArrayItemsCheckboxContainer = ({
  itemLabel,
  selectedItems,
  handleToggleSelection,
  isSelectionMode,
  index,
}: {
  itemLabel: string;
  selectedItems: Map<number, string>;
  handleToggleSelection: (index: number, itemLabel: string) => void;
  isSelectionMode: boolean;
  index: number;
}) =>
  isSelectionMode ? (
    <Checkbox
      key={`${itemLabel}-${index}`}
      id={`select-${itemLabel}-${index}`}
      name={`select-${itemLabel}-${index}`}
      label=""
      aria-label={`Select ${itemLabel} at position ${index}`}
      checked={selectedItems.has(index)}
      onChange={() => handleToggleSelection(index, itemLabel)}
      css={{
        height: '$oneAndHalf',
        '.GDS-checkbox-label': {
          gap: '0',
          paddingBlock: 0,
          paddingInline: 0,
        },
      }}
    />
  ) : null;

const hasArrayMoveDestinationPath = (schema: AnnotatedJsonSchema) =>
  schema?.['x-entity-control']?.type === 'arrayMove' &&
  schema?.['x-entity-control']?.options?.arrayMove?.parentPath;

/**
 * A component that adds selection and move functionality to array controls.
 * @param isSelectionMode - Whether the component is in selection mode
 * @param selectedItems - A map of the selected array items to move by index and label
 * @param getItemLabel - A function to get the label/identifier for each item
 * @param handleMoveClick - A function to handle the move click button
 * @param handleCancelMove - A function to handle the cancel move button
 * @param handleSelectDestination - A function to handle the select destination that opens the move items modal
 * @param handleSelectAll - A function to handle the select all button
 * @param handleMoveItems - A function to handle the move items from one location to another
 * @param isMoveItemsModalOpen - Whether the move items modal is open
 * @param setIsMoveItemsModalOpen - A function to set the move items modal open state
 * @param items - The array of items to move
 * @param schema - The schema of the array
 * @returns A component that adds selection and move functionality to array controls
 */
export const MoveArrayItems = <T extends object>({
  isSelectionMode,
  selectedItems,
  getItemLabel,
  handleMoveClick,
  handleCancelMove,
  handleSelectDestination,
  handleSelectAll,
  handleMoveItems,
  isMoveItemsModalOpen,
  setIsMoveItemsModalOpen,
  items,
  schema,
}: SelectableMoveArrayItemsProps<T>) => {
  const { formatMessage } = useIntl();

  const { formPath, entityRootData, nodeMap, isUsingConfigAppBackend } =
    useEntityDetailsProps();

  const breadcrumbs = getBreadcrumbsForProperty({
    path: formPath ?? '',
    data: entityRootData,
    entityTreeNodeMap: nodeMap,
    translate: formatMessage,
  });

  const moveFromLabel = `${breadcrumbs
    .map(({ label: breadcrumb }) => breadcrumb)
    .join(' → ')} → `;

  // Get the arrayMove path from the schema annotation
  const arrayMovePath = hasArrayMoveDestinationPath(schema);

  // Generate move targets from the arrayMove path defined in the schema
  // Only enable move functionality for entities using the config app backend
  const moveTargets =
    arrayMovePath && isUsingConfigAppBackend
      ? generateMoveTargetsFromJsonPath(
          arrayMovePath,
          entityRootData,
          nodeMap,
          formatMessage,
          formPath,
        )
      : [];

  const allItemLabels = items.map(getItemLabel);
  const isAllSelected =
    allItemLabels.length > 0 &&
    allItemLabels.every((_, index) => selectedItems.has(index));
  const isIndeterminate =
    selectedItems.size > 0 && selectedItems.size < allItemLabels.length;

  if (!isSelectionMode) {
    return (
      <StackLayout orientation="vertical" aria-live="polite">
        {/* Only show move button for entities using the new backend */}
        {isUsingConfigAppBackend && (
          <Button
            priority="tertiary"
            size="medium"
            onClick={handleMoveClick}
            css={{ alignSelf: 'end' }}
          >
            {formatMessage({ id: 'MOVE_ITEMS' })}
          </Button>
        )}
      </StackLayout>
    );
  }

  return (
    <StackLayout
      orientation="vertical"
      spacing="$one"
      horizontalAlignment="stretch"
      aria-live="polite"
    >
      <StackLayout
        orientation="horizontal"
        spacing="$one"
        horizontalAlignment="end"
      >
        <Button
          priority="tertiary"
          size="medium"
          quiet
          color="onSurfaceTextSubdued"
          css={{ alignSelf: 'end' }}
          onClick={handleCancelMove}
        >
          {formatMessage({ id: 'CANCEL_MOVE' })}
        </Button>
        {selectedItems.size > 0 && (
          <Button
            priority="secondary"
            size="medium"
            css={{ alignSelf: 'end' }}
            onClick={handleSelectDestination}
          >
            {formatMessage({ id: 'SELECT_DESTINATION' })}
          </Button>
        )}
      </StackLayout>
      <Checkbox
        id="select-all"
        name="select-all"
        label={formatMessage({ id: 'SELECTOR' })}
        checked={isAllSelected}
        indeterminate={isIndeterminate}
        onChange={handleSelectAll}
        css={{
          '.GDS-checkbox-label-text': {
            fontSize: '$caption',
            color: '$interactiveActionPrimary',
          },
          '.GDS-checkbox-label': { marginInlineStart: '$half' },
        }}
      />
      <MoveItemsModal
        isOpen={isMoveItemsModalOpen}
        onClose={() => setIsMoveItemsModalOpen(false)}
        moveFrom={moveFromLabel}
        moveTargets={moveTargets}
        selectedItems={Array.from(selectedItems.values())}
        onMove={(target) => {
          handleMoveItems(target);
        }}
      />
    </StackLayout>
  );
};
